/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import {
  pipelineService,
  Pipeline,
  PipelineStepDef,
} from '../../services/registryApi';
import toast from 'react-hot-toast';

/** Parse JSON string fields defensively. */
const parseJson = <T,>(raw: any, fallback: T): T => {
  if (!raw) return fallback;
  if (typeof raw === 'object') return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const STEP_TYPES: PipelineStepDef['type'][] = ['training', 'evaluation', 'deployment', 'custom'];
const TRIGGER_TYPES = ['manual', 'cron', 'webhook', 'on_drift'] as const;

const generateStepId = (): string => `step_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

interface StepFormData {
  id: string;
  name: string;
  type: PipelineStepDef['type'];
  depends_on: string[];
  config: string; // JSON string for editing
  timeout_minutes: number;
  retry_count: number;
  collapsed: boolean;
}

const newStep = (): StepFormData => ({
  id: generateStepId(),
  name: '',
  type: 'training',
  depends_on: [],
  config: '{}',
  timeout_minutes: 60,
  retry_count: 0,
  collapsed: false,
});

/* ── Step Card ──────────────────────────────────────────────────────── */
const StepCard: React.FC<{
  step: StepFormData;
  index: number;
  allSteps: StepFormData[];
  onChange: (updated: StepFormData) => void;
  onRemove: () => void;
}> = ({ step, index, allSteps, onChange, onRemove }) => {
  const otherSteps = allSteps.filter((s) => s.id !== step.id);

  const toggleDependency = (depId: string) => {
    const deps = step.depends_on.includes(depId)
      ? step.depends_on.filter((d) => d !== depId)
      : [...step.depends_on, depId];
    onChange({ ...step, depends_on: deps });
  };

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
      {/* Step header */}
      <div
        className="flex items-center justify-between px-5 py-3 bg-gray-800/50 cursor-pointer"
        onClick={() => onChange({ ...step, collapsed: !step.collapsed })}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-gray-500">#{index + 1}</span>
          <span className="text-sm font-medium text-white">{step.name || 'Untitled Step'}</span>
          <span className="px-1.5 py-0.5 text-xs rounded bg-gray-700 text-gray-400 capitalize">
            {step.type}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-1 text-gray-500 hover:text-red-400 rounded hover:bg-red-500/20 transition-colors"
            title="Remove step"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
          {step.collapsed ? (
            <ChevronDownIcon className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronUpIcon className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </div>

      {/* Step body */}
      {!step.collapsed && (
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Step ID */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Step ID</label>
              <input
                type="text"
                value={step.id}
                onChange={(e) => onChange({ ...step, id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500 font-mono"
              />
            </div>
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
              <input
                type="text"
                value={step.name}
                onChange={(e) => onChange({ ...step, name: e.target.value })}
                placeholder="e.g. Train XGBoost"
                className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
              <select
                value={step.type}
                onChange={(e) => onChange({ ...step, type: e.target.value as PipelineStepDef['type'] })}
                className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500"
              >
                {STEP_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            {/* Timeout */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Timeout (min)</label>
              <input
                type="number"
                min={1}
                value={step.timeout_minutes}
                onChange={(e) => onChange({ ...step, timeout_minutes: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            {/* Retry */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Retry Count</label>
              <input
                type="number"
                min={0}
                max={10}
                value={step.retry_count}
                onChange={(e) => onChange({ ...step, retry_count: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Dependencies */}
          {otherSteps.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Dependencies</label>
              <div className="flex flex-wrap gap-2">
                {otherSteps.map((other) => {
                  const selected = step.depends_on.includes(other.id);
                  return (
                    <button
                      key={other.id}
                      type="button"
                      onClick={() => toggleDependency(other.id)}
                      className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                        selected
                          ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      {other.name || other.id}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Config JSON */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Config (JSON)</label>
            <textarea
              value={step.config}
              onChange={(e) => onChange({ ...step, config: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500 font-mono"
              placeholder='{ "learning_rate": 0.01 }'
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Visual Dependency Lines ────────────────────────────────────────── */
const DependencyLines: React.FC<{ steps: StepFormData[] }> = ({ steps }) => {
  if (steps.length < 2) return null;

  const stepIndex = new Map(steps.map((s, i) => [s.id, i]));

  const edges = steps.flatMap((step, toIdx) =>
    step.depends_on
      .filter((depId) => stepIndex.has(depId))
      .map((depId) => ({
        fromIdx: stepIndex.get(depId)!,
        toIdx,
        fromName: steps[stepIndex.get(depId)!]?.name || depId,
        toName: step.name || step.id,
      }))
  );

  if (edges.length === 0) return null;

  return (
    <div className="bg-gray-900/60 rounded-lg border border-gray-800 p-4 mb-4">
      <p className="text-xs font-medium text-gray-500 uppercase mb-3">Dependency Graph</p>
      <div className="flex flex-wrap gap-2">
        {edges.map((edge, i) => (
          <div
            key={i}
            className="flex items-center gap-2 text-xs"
          >
            <span className="px-2 py-1 rounded bg-gray-800 text-gray-300 border border-gray-700">
              {edge.fromName}
            </span>
            <span className="text-orange-500 font-mono">--&gt;</span>
            <span className="px-2 py-1 rounded bg-gray-800 text-gray-300 border border-gray-700">
              {edge.toName}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Tags Input ─────────────────────────────────────────────────────── */
const TagsInput: React.FC<{
  tags: Record<string, string>;
  onChange: (tags: Record<string, string>) => void;
}> = ({ tags, onChange }) => {
  const [input, setInput] = useState('');

  const entries = Object.entries(tags);

  const addTag = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx <= 0) {
      toast.error('Tag format: key=value');
      return;
    }
    const key = trimmed.slice(0, eqIdx).trim();
    const value = trimmed.slice(eqIdx + 1).trim();
    onChange({ ...tags, [key]: value });
    setInput('');
  };

  const removeTag = (key: string) => {
    const next = { ...tags };
    delete next[key];
    onChange(next);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">Tags</label>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="key=value (press Enter)"
          className="flex-1 px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500"
        />
        <button
          type="button"
          onClick={addTag}
          className="px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition-colors"
        >
          Add
        </button>
      </div>
      {entries.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {entries.map(([key, value]) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-orange-500/15 text-orange-400 rounded"
            >
              {key}={value}
              <button
                type="button"
                onClick={() => removeTag(key)}
                className="hover:text-red-400"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main Component ─────────────────────────────────────────────────── */
const PipelineBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<StepFormData[]>([newStep()]);
  const [triggerType, setTriggerType] = useState<string>('manual');
  const [cronExpr, setCronExpr] = useState('');
  const [tags, setTags] = useState<Record<string, string>>({});

  // Load existing pipeline when editing
  const { data: existingPipeline, isLoading: loadingPipeline } = useQuery({
    queryKey: ['pipeline', id],
    queryFn: () => pipelineService.get(id!),
    enabled: isEditing,
    select: (res) => res.data.pipeline,
  });

  useEffect(() => {
    if (existingPipeline) {
      setName(existingPipeline.name);
      setDescription(existingPipeline.description || '');
      setTriggerType(existingPipeline.trigger_type || 'manual');

      const parsed = parseJson<PipelineStepDef[]>(existingPipeline.steps_json, []);
      setSteps(
        parsed.map((s) => ({
          id: s.id,
          name: s.name,
          type: s.type,
          depends_on: s.depends_on || [],
          config: JSON.stringify(s.config || {}, null, 2),
          timeout_minutes: s.timeout_minutes || 60,
          retry_count: s.retry_count || 0,
          collapsed: true,
        }))
      );

      const triggerConfig = parseJson<Record<string, any>>(existingPipeline.trigger_config, {});
      if (triggerConfig.cron_expr) setCronExpr(triggerConfig.cron_expr);

      setTags(parseJson<Record<string, string>>(existingPipeline.tags, {}));
    }
  }, [existingPipeline]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof pipelineService.create>[0]) =>
      pipelineService.create(data),
    onSuccess: () => {
      toast.success('Pipeline created successfully');
      navigate('/registry/pipelines');
    },
    onError: () => {
      toast.error('Failed to create pipeline');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof pipelineService.update>[1]) =>
      pipelineService.update(id!, data),
    onSuccess: () => {
      toast.success('Pipeline updated successfully');
      navigate(`/registry/pipelines/${id}`);
    },
    onError: () => {
      toast.error('Failed to update pipeline');
    },
  });

  const handleStepChange = (index: number, updated: StepFormData) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? updated : s)));
  };

  const handleRemoveStep = (index: number) => {
    const removedId = steps[index].id;
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // Remove the deleted step from dependencies of other steps
      return next.map((s) => ({
        ...s,
        depends_on: s.depends_on.filter((d) => d !== removedId),
      }));
    });
  };

  const handleAddStep = () => {
    setSteps((prev) => [...prev, newStep()]);
  };

  const buildStepDefs = (): PipelineStepDef[] => {
    return steps.map((s) => {
      let config: Record<string, any> = {};
      try {
        config = JSON.parse(s.config);
      } catch {
        // keep empty object
      }
      return {
        id: s.id,
        name: s.name,
        type: s.type,
        depends_on: s.depends_on,
        config,
        timeout_minutes: s.timeout_minutes,
        retry_count: s.retry_count,
      };
    });
  };

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Pipeline name is required');
      return;
    }
    if (steps.some((s) => !s.name.trim())) {
      toast.error('All steps must have a name');
      return;
    }

    const triggerConfig: Record<string, any> = {};
    if (triggerType === 'cron') {
      if (!cronExpr.trim()) {
        toast.error('Cron expression is required for cron trigger');
        return;
      }
      triggerConfig.cron_expr = cronExpr;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      steps: buildStepDefs(),
      trigger_type: triggerType,
      trigger_config: Object.keys(triggerConfig).length > 0 ? triggerConfig : undefined,
      tags: Object.keys(tags).length > 0 ? tags : undefined,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Memoize the dependency line data
  const dependencySteps = useMemo(() => steps, [steps]);

  if (isEditing && loadingPipeline) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-700 border-t-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isEditing ? 'Edit Pipeline' : 'Create Pipeline'}
            </h1>
            <p className="mt-0.5 text-sm text-gray-400">
              Define your multi-step ML workflow
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Pipeline Info */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Pipeline Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. fraud-detection-pipeline"
                className="w-full px-4 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What does this pipeline do?"
                className="w-full px-4 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Trigger Configuration */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">Trigger</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Trigger Type</label>
                <select
                  value={triggerType}
                  onChange={(e) => setTriggerType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500"
                >
                  {TRIGGER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t === 'on_drift' ? 'On Drift' : t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              {triggerType === 'cron' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Cron Expression *</label>
                  <input
                    type="text"
                    value={cronExpr}
                    onChange={(e) => setCronExpr(e.target.value)}
                    placeholder="0 0 * * *"
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-orange-500 font-mono"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Standard cron: minute hour day-of-month month day-of-week
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Steps Builder */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Steps</h2>
              <button
                type="button"
                onClick={handleAddStep}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 text-orange-400 rounded-lg border border-gray-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add Step
              </button>
            </div>

            {/* Visual dependency lines */}
            <DependencyLines steps={dependencySteps} />

            {/* Step cards */}
            <div className="space-y-3">
              {steps.map((step, index) => (
                <StepCard
                  key={step.id + '-' + index}
                  step={step}
                  index={index}
                  allSteps={steps}
                  onChange={(updated) => handleStepChange(index, updated)}
                  onRemove={() => handleRemoveStep(index)}
                />
              ))}
            </div>

            {steps.length === 0 && (
              <div className="text-center py-8 bg-gray-900 rounded-xl border border-dashed border-gray-700">
                <Cog6ToothIcon className="mx-auto h-10 w-10 text-gray-600" />
                <p className="mt-2 text-sm text-gray-400">No steps defined yet.</p>
                <button
                  type="button"
                  onClick={handleAddStep}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm text-orange-400 hover:text-orange-300"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add your first step
                </button>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
            <TagsInput tags={tags} onChange={setTags} />
          </div>

          {/* Save */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : isEditing ? 'Update Pipeline' : 'Create Pipeline'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineBuilder;
